import { useEffect, useRef } from 'react';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import type { Finding, FindingStatement } from '@/models/report';
import {
  EMPTY_STATEMENTS,
  groupFindingStatements,
  statementSignature,
} from '@/features/report/Findings/FindingInput/utils/statements';
import type { ReportEditBaseline } from '../utils/reportEditBaseline';

/**
 * Populate the findings editor state from a loaded report.
 *
 * First load adopts the stored statements; a post-save refetch of the same
 * report leaves in-flight edits alone.
 */
export function useSeedReportFindings(
  currentReport: { uuid: string; findings: Finding[] } | null,
  baselineRef: MutableRefObject<ReportEditBaseline | null>,
  setFindings: Dispatch<SetStateAction<Record<string, FindingStatement[]>>>
) {
  const lastUuidRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!currentReport) return;
    const grouped = groupFindingStatements(currentReport.findings);
    const baseline = baselineRef.current;
    const firstSeed = lastUuidRef.current !== currentReport.uuid;
    lastUuidRef.current = currentReport.uuid;
    setFindings((prev) => {
      const next: Record<string, FindingStatement[]> = { ...prev };
      Object.entries(grouped).forEach(([fc, rows]) => {
        // The baseline drops empty classes — absent means "was empty".
        const baseSig = baseline?.findingByClass.get(fc) ?? EMPTY_STATEMENTS;
        const dirty =
          !firstSeed &&
          !!baseline &&
          statementSignature(prev[fc] ?? []) !== baseSig;
        if (!dirty) next[fc] = rows;
      });
      return next;
    });
  }, [currentReport, baselineRef, setFindings]);
}
